import React, { PropsWithChildren, useCallback } from 'react';
import TagManager from 'react-gtm-module';
import { createContext, useContext } from 'react';
import { webConfig } from '../../utils/webConfig';
import {
    getAppInfo,
    pushEvent,
    addProfile,
    trackLinks,
    CleverTapDonorProfile,
    extendProfile,
} from './clevertap';
import { Donor } from '../../providers/DonorProvider/interfaces';
import { FeatureFlag } from '../../constants';
import { useFeatureFlag } from '../../providers';

const addProfileEventName = 'Add profile';

export interface PushEventPayload {
    [key: string]: any; // This allows any string key with any value type
    name?: string;
    location?: string;
    screen?: string;
    type?: string;
    action?: string;
}
let lastEventName: string | undefined = undefined;
let lastEventPayload: PushEventPayload | undefined = undefined;
const propertiesToCompare = ['name', 'location', 'screen', 'type', 'action'];
const isSameEvent = (
    eventName: string,
    payload: PushEventPayload | undefined,
) => {
    if (eventName !== lastEventName || !lastEventPayload || !payload) {
        return false;
    }
    return (
        eventName === lastEventName &&
        propertiesToCompare.every(
            (property) => payload[property] === lastEventPayload?.[property],
        )
    );
};

const defaultPushEvent = (
    eventName: string,
    payload: PushEventPayload | undefined = {},
): void =>
    // eslint-disable-next-line
    console.log(eventName, payload);

const defaultAddProfile = (donor: CleverTapDonorProfile): void =>
    // eslint-disable-next-line
    console.log('Add profile', donor);

const defaultExtendProfile = (gifts?: number, recurring?: boolean): void =>
    // eslint-disable-next-line
    console.log('Extend profile', gifts, recurring);

const defaultTrackLinks = (link: string, properties?: object): void =>
    // eslint-disable-next-line
    console.log(link, properties);

export const PushEventsContext = createContext<{
    pushEvent: (eventName: string, payload?: object) => unknown;
    addProfile: (donor: CleverTapDonorProfile) => unknown;
    trackLinks: (link: string) => unknown;
    extendProfile: (gifts?: number, recurring?: boolean) => unknown;
}>({
    pushEvent: defaultPushEvent,
    addProfile: defaultAddProfile,
    trackLinks: defaultTrackLinks,
    extendProfile: defaultExtendProfile,
});

export const usePushEvents = () => useContext(PushEventsContext);

const PushEventsProvider: React.FC<
    PropsWithChildren & {
        donorEmailRef: React.MutableRefObject<Donor | undefined>;
    }
> = ({ donorEmailRef, children }) => {
    const { isFeatureEnabled } = useFeatureFlag();

    const handleEvents = useCallback(
        (eventName: string, payload?: PushEventPayload) => {
            if (isSameEvent(eventName, payload)) {
                return;
            }
            lastEventName = eventName;
            lastEventPayload = payload;
            const properties = {
                ...(payload || {}),
                ...getAppInfo(),
                donor: donorEmailRef.current?.email || 'unknown',
            };
            const enabled = isFeatureEnabled(FeatureFlag.OGA_CLEVERTAP);
            if (enabled && !webConfig.isPrerenderBot) {
                pushEvent(eventName, properties);
                TagManager.dataLayer({
                    dataLayer: {
                        event: eventName,
                        eventProps: properties,
                    },
                });
            }
        },
        [donorEmailRef, isFeatureEnabled],
    );

    const handleAddProfile = useCallback(
        (donor?: CleverTapDonorProfile) => {
            if (!donor) return;
            if (lastEventName === addProfileEventName) return;
            lastEventName = addProfileEventName;
            const enabled = isFeatureEnabled(FeatureFlag.OGA_CLEVERTAP);
            if (enabled && !webConfig.isPrerenderBot) {
                if (
                    window.clevertap?.profile?.[0]?.Site.Identity ===
                    donor.donor_id?.toString()
                )
                    return;
                addProfile(donor);
            } else if (!webConfig.isProduction) {
                defaultAddProfile(donor);
            } else {
                console.log('Unable to publish clevertap profile');
            }
        },
        [isFeatureEnabled],
    );

    const handleTrackLinks = useCallback(
        (link: string) => {
            const properties = {
                ...getAppInfo(),
                donor: donorEmailRef.current?.email || 'unknown',
            };
            const enabled = isFeatureEnabled(FeatureFlag.OGA_CLEVERTAP);
            if (enabled && !webConfig.isPrerenderBot) {
                trackLinks(link, properties);
            } else if (!webConfig.isProduction) {
                defaultTrackLinks(link, properties);
            }
        },
        [donorEmailRef, isFeatureEnabled],
    );

    const handleExtendProfile = useCallback(
        (gifts?: number, recurring?: boolean) => {
            extendProfile(gifts, recurring);
        },
        [],
    );

    return (
        <PushEventsContext.Provider
            value={{
                pushEvent: handleEvents,
                addProfile: handleAddProfile,
                extendProfile: handleExtendProfile,
                trackLinks: handleTrackLinks,
            }}
        >
            {children}
        </PushEventsContext.Provider>
    );
};

export default PushEventsProvider;
